import React, { createContext, useContext, useState, useEffect } from "react";
import axiosInstance from "../config/axiosInstance";

const AuthContext = createContext();

/**
 * Proveedor de contexto de autenticación.
 * Administra el estado de sesión del usuario, incluyendo token, datos del usuario y permisos.
 */
export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false); // Estado de sesión activa
  const [isAuthChecking, setIsAuthChecking] = useState(true); // Validación inicial
  const [user, setUser] = useState(null); // Objeto con los datos del usuario y permisos

  /**
   * Realiza el login, almacena el token y extrae la información del payload JWT.
   * @param {string} token - Token JWT recibido desde backend
   */
  const login = (token) => {
    localStorage.setItem("tokenNovianCore", token);

    try {
      const payload = JSON.parse(
        decodeURIComponent(escape(atob(token.split(".")[1])))
      );

      const userId = payload.sub;
      const fullName = payload.fullName || "";
      const role = payload.role || "";
      const companyId = payload.companyId || null;
      const permissions = payload.permission || [];

      // Guardar en localStorage
      localStorage.setItem("userIdNovianCore", userId);
      localStorage.setItem("fullNameNovianCore", fullName);
      localStorage.setItem("roleNovianCore", role);
      localStorage.setItem(
        "permissionsNovianCore",
        JSON.stringify(permissions)
      );
      if (companyId) {
        localStorage.setItem("companyIdNovianCore", companyId);
      } else {
        localStorage.removeItem("companyIdNovianCore");
      }

      // Setear en estado
      setUser({ userId, fullName, role, companyId, permissions });
    } catch (err) {
      console.error("Error al decodificar el token JWT:", err);
    }

    axiosInstance.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    setIsAuthenticated(true);
  };

  /**
   * Cierra sesión limpiando el estado y el localStorage.
   */
  const logout = () => {
    localStorage.removeItem("tokenNovianCore");
    localStorage.removeItem("userIdNovianCore");
    localStorage.removeItem("fullNameNovianCore");
    localStorage.removeItem("roleNovianCore");
    localStorage.removeItem("companyIdNovianCore");
    localStorage.removeItem("permissionsNovianCore");

    delete axiosInstance.defaults.headers.common["Authorization"];
    setIsAuthenticated(false);
    setUser(null);
  };

  /**
   * Al cargar la app, verifica el token y reconstruye el estado del usuario desde localStorage.
   */
  useEffect(() => {
    const token = localStorage.getItem("tokenNovianCore");

    if (token) {
      axiosInstance.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${token}`;
      axiosInstance
        .get("/auth/validate-token")
        .then(() => {
          const userId = localStorage.getItem("userIdNovianCore");
          const fullName = localStorage.getItem("fullNameNovianCore");
          const role = localStorage.getItem("roleNovianCore");
          const companyId = localStorage.getItem("companyIdNovianCore");
          const permissions = JSON.parse(
            localStorage.getItem("permissionsNovianCore") || "[]"
          );

          setUser({ userId, fullName, role, companyId, permissions });
          setIsAuthenticated(true);
        })
        .catch((error) => {
          console.error("Token validation failed:", error);
          logout();
        })
        .finally(() => {
          setIsAuthChecking(false);
        });
    } else {
      setIsAuthChecking(false);
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isAuthChecking,
        login,
        logout,
        user, // user incluye permisos y companyId
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Hook personalizado para acceder al contexto de autenticación.
 */
export const useAuth = () => useContext(AuthContext);
